import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Label } from '../ui/label'
import { Button } from '../ui/button'
import { Textarea } from '../ui/textarea'
import { cn } from '../../lib/utils'
import type { MatrixWeek } from './matrix-helpers'
import type { TrainingPlanWeekKind } from '../../types/training-plan'

interface WeekDialogProps {
  week: MatrixWeek | null
  onOpenChange: (open: boolean) => void
  onSave: (weekNumber: number, kind: TrainingPlanWeekKind, description: string | null) => void
}

const KINDS: TrainingPlanWeekKind[] = ['base', 'rest']

// Selected styling per kind: base is orange, rest is sky/azure.
function selectedClass(kind: TrainingPlanWeekKind): string {
  return kind === 'rest'
    ? 'border-sky-500 bg-sky-500/15 text-sky-600 dark:text-sky-400'
    : 'border-orange-500 bg-orange-500/15 text-orange-600 dark:text-orange-400'
}

/** Edits a single week's type (base/rest) and free-text description. */
export function WeekDialog({ week, onOpenChange, onSave }: WeekDialogProps) {
  const { t } = useTranslation()
  const [kind, setKind] = useState<TrainingPlanWeekKind>('base')
  const [description, setDescription] = useState('')

  useEffect(() => {
    if (week) {
      setKind(week.kind)
      setDescription(week.description ?? '')
    }
  }, [week])

  function handleSave() {
    if (!week) return
    onSave(week.week_number, kind, description.trim() === '' ? null : description)
    onOpenChange(false)
  }

  return (
    <Dialog open={week !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            {week ? t('trainingPlans.builder.weekDialogTitle', { number: week.week_number }) : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('trainingPlans.builder.weekTypeLabel')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {KINDS.map((k) => {
                const active = kind === k
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    aria-pressed={active}
                    className={cn(
                      'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                      active ? selectedClass(k) : 'border-input text-muted-foreground hover:bg-muted'
                    )}
                  >
                    {t(`trainingPlans.weekKind.${k}`)}
                  </button>
                )
              })}
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label className="text-xs">{t('trainingPlans.builder.weekDescriptionLabel')}</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('trainingPlans.builder.weekDescriptionPlaceholder')}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" onClick={handleSave}>
            {t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
